// @ts-strict-ignore
import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { PrintLabelingGenerator } from '@project/components/label/print-labeling/print-labeling-generator';
import { PrintLabelingArticleInfo } from '@project/components/article/model/print-labeling-article-info';
import { StringUtils } from '@shared/util/string-utils';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ArrayUtils } from '@shared/util/array-utils';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { UserSettings } from '@user/model/user-settings';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';

export enum SmartScriptVersion {
  smartScript3 = 3,
  smartScript4 = 4,
}

@Component({
  standalone: false,
  selector: 'print-labeling',
  templateUrl: './print-labeling.component.html',
})
export class PrintLabelingComponent implements OnInit, AfterViewInit {
  @Input() isLabelMaterialSelectionAvailable: boolean;
  @Input() isSaveOperationAvailable: boolean;

  public labelingArticleInfos: PrintLabelingArticleInfo[] = [];
  public readonly smartScriptVersions: SmartScriptVersion[] = [SmartScriptVersion.smartScript3, SmartScriptVersion.smartScript4];

  public readonly CUTMARK_NONE: string = 'NONE';
  public readonly CUTMARK_START: string = 'START';
  public readonly CUTMARK_SECTION: string = 'SECTION';

  public selectedCutmarkOption: string = this.CUTMARK_NONE;
  private selectedPrintLabelingArticleInfo: PrintLabelingArticleInfo;

  private readonly CHECKBOX_ISSELECTED: string = 'isSelected';
  private readonly CHECKBOX_ADDCUTMARK: string = 'addCutMark';

  public openArticleRows: number[] = [1];
  public smartScriptVersion: number = undefined;

  constructor(
    private bsModalRef: BsModalRef,
    private userSettings: UserSettings,
    private printLabelingGenerator: PrintLabelingGenerator,
    private overlayService: OverlayService,
    private localeResourceManager: LocaleResourceManager
  ) {}

  public ngOnInit(): void {
    this.overlayService.requestOverlayShow(OverlayBackground.color, true);
  }

  public ngAfterViewInit(): void {
    setTimeout(() => this.initData(), 0);
  }

  private initData(): void {
    this.labelingArticleInfos = this.printLabelingGenerator.getPrintLabelingArticleInfosForDataProvider();
    this.labelingArticleInfos.sort(this.printLabelingArticleInfoCompareFunction);
    if (ArrayUtils.isNotEmpty(this.labelingArticleInfos)) {
      this.toggleParentAndChildrenIsSelected(this.labelingArticleInfos[0]);
    }
    this.printLabelingGenerator.collectExcludedCharCodes();
    this.overlayService.requestOverlayHide(OverlayBackground.color);
    this.smartScriptVersion = this.userSettings.lastSelectedSmartScriptVersion;
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  private printLabelingArticleInfoCompareFunction(itemA: PrintLabelingArticleInfo, itemB: PrintLabelingArticleInfo): number {
    return StringUtils.stringCompare(itemA.label + itemA.labelForSubNodes, itemB.label + itemA.labelForSubNodes);
  }

  public selectSectionCutmarks(): void {
    if (this.selectedPrintLabelingArticleInfo
        && this.selectedPrintLabelingArticleInfo.hasCutmarkAbility
        && !this.selectedPrintLabelingArticleInfo.addCutMark) {
      this.setCutmarksProperty();
    }
  }

  public selectNoCutmarks(): void {
    if (this.selectedPrintLabelingArticleInfo
        && this.selectedPrintLabelingArticleInfo.hasCutmarkAbility) {
      this.setCutmarksProperty(false);
    }
  }

  public selectStartCutmark(): void {
    this.setCutmarksProperty(false);
    if (this.selectedPrintLabelingArticleInfo
        && this.selectedPrintLabelingArticleInfo.hasCutmarkAbility) {
      if (this.selectedPrintLabelingArticleInfo.children?.length > 0) {
        this.selectedPrintLabelingArticleInfo.children[0].addCutMark = true;
      } else {
        this.selectedPrintLabelingArticleInfo.addCutMark = true;
      }
    }
  }

  private setCutmarksProperty(isSelect: boolean = true): void {
    if (this.selectedPrintLabelingArticleInfo.children === undefined || this.selectedPrintLabelingArticleInfo.children.length === 0) {
      this.selectedPrintLabelingArticleInfo[this.CHECKBOX_ADDCUTMARK] = isSelect;
    } else {
      this.selectedPrintLabelingArticleInfo[this.CHECKBOX_ADDCUTMARK] = isSelect;
      this.selectedPrintLabelingArticleInfo.children.forEach(child => child[this.CHECKBOX_ADDCUTMARK] = isSelect);
    }
  }

  public toggleParentAndChildrenIsSelected(item: PrintLabelingArticleInfo): void {
    this.toggleMaterialSelection(item);
    this.unselectOtherMaterialSelection(item);
    if (!item.isSelected) {
      item.addCutMark = false;
      if (item.children) {
        item.children.forEach((child) => (child.addCutMark = false));
      }
    }
    this.selectCutmarksOnSelectionChange();
  }

  public toggleParentNoChildrenIsSelected(item: PrintLabelingArticleInfo): void {
    this.toggleMaterialSelection(item);
    this.unselectOtherMaterialSelection(item);
    if (!item.isSelected) {
      item.addCutMark = false;
    }
    this.selectCutmarksOnSelectionChange();
  }

  private selectCutmarksOnSelectionChange(): void {
    switch (this.selectedCutmarkOption) {
      case this.CUTMARK_NONE:
        this.selectNoCutmarks();
        break;
      case this.CUTMARK_SECTION:
        this.selectSectionCutmarks();
        break;
      case this.CUTMARK_START:
        this.selectStartCutmark();
        break;
    }
  }

  public isSectionCutmarkOption(): boolean {
    return this.selectedCutmarkOption === this.CUTMARK_SECTION;
  }

  public isStartCutmarkOption(item: PrintLabelingArticleInfo): boolean {
    return this.selectedCutmarkOption === this.CUTMARK_START
      || (item.children === undefined && this.selectedCutmarkOption !== this.CUTMARK_NONE);
  }

  public isNoCutmarkOption(): boolean {
    return this.selectedCutmarkOption === this.CUTMARK_NONE;
  }

  private unselectOtherMaterialSelection(item: PrintLabelingArticleInfo): void {
    let selectedChildren = [];
    selectedChildren = item.children?.filter((child) => child.isSelected);
    if (item.isSelected || ArrayUtils.isNotEmpty(selectedChildren)) {
      let previousSelections: PrintLabelingArticleInfo[] = this.getSelectedItems();
      for (let i = 0; i < previousSelections.length; i++) {
        if (previousSelections[i] !== item && this.isChildMaterialNotSelected(item, previousSelections[i])) {
          this.toggleMaterialSelection(previousSelections[i], true);
          previousSelections[i].addCutMark = false;
          if (previousSelections[i].children) {
            previousSelections[i].children.forEach((child) => (child.addCutMark = false));
          }
        }
      }
    }
  }

  private isChildMaterialNotSelected (item: PrintLabelingArticleInfo, selection: PrintLabelingArticleInfo): boolean {
    if (selection.articleUsageIds) {
      let selectedChildren = item.children?.filter((child) => {
        return child.articleUsageIds[0] === selection.articleUsageIds[0];
      });
      return ArrayUtils.isEmpty(selectedChildren);
    } else {
      return true;
    }
  }

  private toggleMaterialSelection(item: PrintLabelingArticleInfo, isOldSelection: boolean = false): void {
    const property: string = this.CHECKBOX_ISSELECTED;
    if (!isOldSelection) {
      this.selectedPrintLabelingArticleInfo = item.isSelected ? null : item;
    }
    if (item.children === undefined || item.children.length === 0) {
      item[property] = !item[property];
    } else {
      item[property] = !item[property];
      item.children.forEach(child => child[property] = item[property])
    }
  }

  public getSelectedItems(): PrintLabelingArticleInfo[] {
    let selectedItems: PrintLabelingArticleInfo[] = [];

    for (let i = 0; i < this.labelingArticleInfos.length; i++) {
      let currentItem = this.labelingArticleInfos[i];
      if (currentItem.isSelected) {
        selectedItems.push(currentItem);
      } else if (currentItem.children && currentItem.children.length !== 0) {
        this.checkForSelectedChildren(selectedItems, currentItem);
      }
    }
    return selectedItems;
  }

  private checkForSelectedChildren(selectedItems: PrintLabelingArticleInfo[], currentItem: PrintLabelingArticleInfo) {
    for (let j = 0; j < currentItem.children.length; j++) {
      if (currentItem.children[j].isSelected) {
        selectedItems.push(currentItem.children[j]);
      }
    }
  }

  public onMaterialSelectionChange(item: PrintLabelingArticleInfo): void {
    this.unselectOtherMaterialSelection(item);
    let allTrue: boolean = true;
    item.children.forEach((child) => {
      if (!child.isSelected) {
        allTrue = false;
        item.isSelected = false;
        child.addCutMark = false;
      }
    });
    item.isSelected = allTrue;
    if (!allTrue) {
      item.addCutMark = false;
    }
  }

  public printButtonClick(): void {
    if (!this.isLabelMaterialSelectionAvailable) {
      this.close();
    }
    this.saveSmartScriptVersion();
    this.printLabelingGenerator.exportPrintLabeling(this.getSelectedItems(), false, this.selectedCutmarkOption);
  }

  public saveButtonClick(): void {
    this.saveSmartScriptVersion();
    this.printLabelingGenerator.exportPrintLabeling(this.getSelectedItems(), true, this.selectedCutmarkOption);
  }

  public toggleArticleRow(id: number): void {
    const index: number = this.openArticleRows.indexOf(id);
    if (index !== -1) {
      this.openArticleRows.splice(index, 1);
      return;
    }
    this.openArticleRows.push(id);
  }

  private saveSmartScriptVersion() {
    this.userSettings.setUserSettingValue("lastSelectedSmartScriptVersion", this.smartScriptVersion);
    this.userSettings.saveUserSettings();
  }

  public openHELPLink(): void {
    window.open(this.localeResourceManager.getValueByKey(`helplink.printmarking`), `_blank`);
  }
}
